/* Contour-only enhancement. Shopify remains authoritative for stock and discounts. */
(() => {
  const cents = value => {
    const match = /^(\d+)(?:\.(\d{1,2}))?$/.exec(String(value ?? '').trim());
    if (!match) return null;
    const amount = Number(match[1]) * 100 + Number((match[2] || '').padEnd(2, '0'));
    return Number.isSafeInteger(amount) ? amount : null;
  };

  function priceSelection(variants, config) {
    if (!variants.length || variants.some(variant => !variant || !Number.isSafeInteger(variant.price) || variant.price < 0)) return null;
    const subtotal = variants.reduce((sum, variant) => sum + variant.price, 0);
    const compare = variants.reduce((sum, variant) => sum + Math.max(variant.price, Number(variant.compare_at_price) || 0), 0);
    let each = 0;
    // Fixed discounts are configured in shop currency. Do not subtract USD cents from another currency.
    if (config.currency === 'USD' && config.storeCurrency === 'USD' && config.discounts) {
      for (const quantity of [2, 3]) {
        const amount = cents(config.discounts[quantity]?.each);
        if (variants.length >= quantity && amount !== null) each = Math.max(each, amount);
      }
    }
    const total = variants.reduce((sum, variant) => sum + Math.max(0, variant.price - each), 0);
    return { subtotal, compare, total, savings: subtotal - total, each };
  }

  function buildItems(variants) {
    if (!variants.length || variants.some(variant => !variant || !variant.available || !/^[1-9]\d*$/.test(String(variant.id)))) {
      throw new Error('Choose an available color and size for every belt.');
    }
    const grouped = new Map();
    for (const variant of variants) {
      const key = String(variant.id);
      const item = grouped.get(key) || { id: variant.id, quantity: 0 };
      item.quantity++;
      grouped.set(key, item);
    }
    for (const variant of variants) {
      if (variant.inventory_management === 'shopify' && variant.inventory_policy !== 'continue' &&
          Number.isInteger(variant.inventory_quantity) && grouped.get(String(variant.id)).quantity > variant.inventory_quantity) {
        throw new Error('There is not enough stock for this selection. Choose another color or size.');
      }
    }
    return [...grouped.values()];
  }

  async function addToDrawer({ items, drawer, opener, action, pageUrl }, environment = globalThis) {
    if (!drawer || typeof drawer.getSectionsToRender !== 'function' || typeof drawer.renderContents !== 'function') {
      throw new Error('The cart is not ready yet. Please refresh and try again.');
    }
    const page = new URL(pageUrl);
    const endpoint = new URL(action, page);
    if (endpoint.origin !== page.origin || !/\/cart\/add(?:\.js)?\/?$/.test(endpoint.pathname)) {
      throw new Error('Unable to connect to this store’s cart.');
    }
    endpoint.pathname = endpoint.pathname.replace(/\/$/, '').replace(/\.js$/, '') + '.js';
    const sections = drawer.getSectionsToRender();
    const ids = [...new Set(sections.map(section => section.id))];
    if (!ids.length || ids.length > 5) throw new Error('The cart is not ready yet. Please refresh and try again.');
    const valid = html => sections.every(section => typeof html?.[section.id] === 'string' &&
      new environment.DOMParser().parseFromString(html[section.id], 'text/html').querySelector(section.selector || '.shopify-section'));
    const getJSON = async url => {
      const response = await environment.fetch(String(url), { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Cart refresh unavailable.');
      return response.json();
    };
    const sectionUrl = new URL(page);
    sectionUrl.searchParams.set('sections', ids.join(','));
    const render = state => {
      if (!valid(state.sections)) throw new Error('Cart refresh unavailable.');
      drawer.setActiveElement?.(opener);
      drawer.renderContents(state);
      if (Number.isInteger(state.item_count)) {
        drawer.classList.toggle('is-empty', state.item_count === 0);
        drawer.querySelector?.('.drawer__inner')?.classList.toggle('is-empty', state.item_count === 0);
      } else drawer.classList.remove('is-empty');
    };
    let added = false;
    let serverMessage = '';
    try {
      const response = await environment.fetch(endpoint.href, {
        method: 'POST', credentials: 'same-origin',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items, sections: ids, sections_url: page.pathname + page.search })
      });
      const state = await response.json();
      if (!response.ok || state.status) {
        serverMessage = typeof state.description === 'string' ? state.description : typeof state.message === 'string' ? state.message : '';
        throw new Error('Cart add was not confirmed.');
      }
      added = true;
      if (!valid(state.sections)) state.sections = await getJSON(sectionUrl);
      render(state);
      return state;
    } catch (failure) {
      // Even a 422 can add the available stock. Never repeat a POST after an error.
      if (!added) {
        try {
          const cartUrl = new URL(endpoint);
          cartUrl.pathname = cartUrl.pathname.replace(/\/add\.js$/, '.js');
          const [cart, html] = await Promise.all([getJSON(cartUrl), getJSON(sectionUrl)]);
          render({ ...cart, sections: html });
        } catch (_) { /* The customer can still inspect the ordinary cart link. */ }
      }
      const error = new Error(added
        ? 'Added to your cart, but the drawer could not refresh. Open your cart before adding again.'
        : (serverMessage ? serverMessage + ' ' : 'We could not confirm the add. ') + 'Please check your cart before trying again; some items may already have been added.');
      error.cartAdded = added;
      throw error;
    }
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { cents, priceSelection, buildItems, addToDrawer };
  if (typeof customElements === 'undefined' || customElements.get('aven-contour-sets')) return;

  class AvenContourSets extends HTMLElement {
    connectedCallback() { this.frame = requestAnimationFrame(() => this.initialize()); }
    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.abort?.abort();
      this.section?.classList.remove('contour-sets-active');
    }
    initialize() {
      this.abort?.abort();
      this.section = document.getElementById('MainProduct-' + this.dataset.section);
      this.form = document.getElementById(this.dataset.productForm);
      this.button = document.getElementById('ProductSubmitButton-' + this.dataset.section);
      try { this.config = JSON.parse(this.querySelector('[data-set-config]').textContent); }
      catch (_) { return; }
      const { variants, colorIndex, sizeIndex } = this.config;
      this.cards = [...this.querySelectorAll('[data-set-card]')];
      if (!this.section?.contains(this) || !this.section.contains(this.form) || !this.button ||
          !Array.isArray(variants) || !variants.length || !Number.isInteger(colorIndex) || !Number.isInteger(sizeIndex) ||
          colorIndex === sizeIndex || variants.some(variant => !Number.isSafeInteger(variant.price) || variant.price < 0 ||
            !Array.isArray(variant.options) || variant.options.length !== 2 ||
            typeof variant.options[colorIndex] !== 'string' || typeof variant.options[sizeIndex] !== 'string') ||
          this.cards.length !== 3 || new Set(this.cards.map(card => Number(card.dataset.setCard))).size !== 3 ||
          this.cards.some(card => ![1, 2, 3].includes(Number(card.dataset.setCard)) ||
            card.querySelectorAll('[data-set-row]').length !== Number(card.dataset.setCard) || !card.querySelector('[data-set-quantity]') || !card.querySelector('[data-set-details]') ||
            [...card.querySelectorAll('[data-set-row]')].some(row => !row.querySelector('[data-set-color]') || !row.querySelector('[data-set-size]')))) return;
      try { this.money = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: this.config.currency }); }
      catch (_) { return; }
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.busy = false;
      this.serverError = '';
      this.label = this.button.querySelector('.main-atc__label__text');
      this.originalLabel = this.originalLabel || this.label?.textContent.trim() || 'Add to cart';
      this.addEventListener('click', event => {
        const swatch = event.target.closest('[data-set-preview-color]');
        if (!swatch || !this.contains(swatch) || swatch.disabled || this.busy) return;
        this.choosePreviewColor(swatch.dataset.setPreviewColor);
      }, { signal });
      this.addEventListener('change', event => {
        if (!event.target.matches('[data-set-quantity], [data-set-color], [data-set-size]')) return;
        const card = event.target.closest('[data-set-card]');
        if (card) card.querySelector('[data-set-quantity]').checked = true;
        this.serverError = '';
        this.update(event.target.closest('[data-set-row]'));
      }, { signal });
      // Capture before the native product-form's target listener, for this form only.
      this.section.addEventListener('submit', event => {
        if (event.target !== this.form) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.submit();
      }, { capture: true, signal });
      window.addEventListener('pageshow', () => { if (!this.busy) this.update(); }, { signal });
      this.update();
      this.section.classList.add('contour-sets-active');
    }
    variantFor(row) {
      const color = row.querySelector('[data-set-color]').value;
      const size = row.querySelector('[data-set-size]').value;
      return this.config.variants.find(variant => variant.options[this.config.colorIndex] === color && variant.options[this.config.sizeIndex] === size);
    }
    activeCard() { return this.cards.find(card => card.querySelector('[data-set-quantity]').checked); }
    selection() { return [...(this.activeCard()?.querySelectorAll('[data-set-row]') || [])].map(row => this.variantFor(row)); }
    choosePreviewColor(color) {
      if (this.busy || !this.config.variants.some(variant => variant.available && variant.options[this.config.colorIndex] === color)) return;
      for (const card of this.cards) {
        const select = card.querySelector('[data-set-row]')?.querySelector('[data-set-color]');
        if (select) select.value = color;
      }
      this.serverError = '';
      this.update(this.activeCard()?.querySelector('[data-set-row]'));
    }
    syncColorPreview(color) {
      const name = this.querySelector('[data-set-preview-name]');
      if (name) name.textContent = color;
      this.querySelectorAll('[data-set-preview-color]').forEach(button => {
        const value = button.dataset.setPreviewColor;
        button.setAttribute('aria-pressed', String(value === color));
        button.disabled = this.busy || !this.config.variants.some(variant => variant.available && variant.options[this.config.colorIndex] === value);
        const image = button.querySelector('[data-set-preview-image]');
        const source = this.config.swatches?.[value];
        if (image && source && image.getAttribute('src') !== source) image.setAttribute('src', source);
      });
    }
    syncNative(row) {
      if (!row) return;
      const variant = this.variantFor(row);
      const color = row.querySelector('[data-set-color]').value;
      this.syncColorPreview(color);
      const picker = document.getElementById('variant-selects-' + this.dataset.section);
      if (variant) {
        this.form.querySelectorAll('[name="id"]').forEach(input => { input.value = variant.id; });
        if (picker) {
          picker.querySelectorAll('[data-option-index]').forEach(group => {
            const value = variant.options[Number(group.dataset.optionIndex)];
            group.querySelectorAll('input[type="radio"]').forEach(input => { input.checked = input.value === value; });
          });
          picker.querySelectorAll('select[name^="options["]').forEach((select, index) => {
            const suffix = /-(\d+)$/.exec(select.id);
            select.value = variant.options[suffix ? Number(suffix[1]) : index];
          });
          picker.currentVariant = variant;
          picker.options = variant.options.slice();
        }
      }
      this.dispatchEvent(new CustomEvent('aven:contour-color', { bubbles: true, detail: { color, variant: variant || null } }));
    }
    update(changedRow) {
      const active = this.activeCard();
      for (const card of this.cards) {
        const selected = card === active;
        card.classList.toggle('is-selected', selected);
        card.querySelector('[data-set-details]').hidden = !selected;
        card.querySelector('[data-set-quantity]').disabled = this.busy;
        const rows = [...card.querySelectorAll('[data-set-row]')];
        for (const row of rows) {
          const color = row.querySelector('[data-set-color]');
          const size = row.querySelector('[data-set-size]');
          color.disabled = size.disabled = this.busy || !selected;
          for (const option of color.options) option.disabled = !this.config.variants.some(variant => variant.available && variant.options[this.config.colorIndex] === option.value);
          for (const option of size.options) option.disabled = !this.config.variants.some(variant => variant.available && variant.options[this.config.colorIndex] === color.value && variant.options[this.config.sizeIndex] === option.value);
          const variant = this.variantFor(row);
          row.setAttribute('aria-invalid', String(!variant?.available));
          const thumb = row.querySelector('[data-set-thumb]');
          const source = this.config.swatches?.[color.value];
          if (thumb && source) thumb.src = source;
        }
        const price = priceSelection(rows.map(row => this.variantFor(row)), this.config);
        const total = card.querySelector('[data-set-total]');
        const compare = card.querySelector('[data-set-compare]');
        const saving = card.querySelector('[data-set-savings]');
        const unit = card.querySelector('[data-set-unit-price]');
        if (total) total.textContent = price ? this.money.format(price.total / 100) : 'Unavailable';
        if (unit && rows.length > 1) {
          unit.hidden = !price;
          unit.textContent = price ? (this.config.labels?.quantityEach || '__COUNT__ belts · __AMOUNT__ each')
            .replace('__COUNT__', String(rows.length))
            .replace('__AMOUNT__', this.money.format(price.total / rows.length / 100)) : '';
        }
        if (compare) {
          compare.hidden = !price || price.compare <= price.total;
          compare.textContent = price ? this.money.format(price.compare / 100) : '';
        }
        if (saving) {
          saving.hidden = !price?.savings;
          saving.textContent = price?.savings ? 'Save an extra ' + this.money.format(price.savings / 100) : '';
          saving.setAttribute('aria-label', price?.savings ? saving.textContent + ' compared with buying separately' : '');
        }
      }
      let selectionError = '';
      try { buildItems(this.selection()); } catch (error) { selectionError = error.message; }
      const disabled = this.busy || !!selectionError;
      this.button.disabled = disabled;
      this.button.setAttribute('aria-disabled', String(disabled));
      this.button.classList.toggle('loading', this.busy);
      this.button.querySelector('.loading-overlay__spinner')?.classList.toggle('hidden', !this.busy);
      if (this.label) this.label.textContent = this.busy ? 'Adding…' : selectionError ? 'Choose available options' : this.originalLabel;
      this.form.setAttribute('aria-busy', String(this.busy));
      const error = this.querySelector('[data-set-error]');
      if (error) { error.textContent = selectionError || this.serverError; error.hidden = !error.textContent; }
      this.previewRow = changedRow || (this.previewRow?.closest('[data-set-card]') === active ? this.previewRow : active?.querySelector('[data-set-row]'));
      this.syncNative(this.previewRow);
    }
    async submit() {
      if (this.busy) return;
      let items;
      try { items = buildItems(this.selection()); }
      catch (error) { this.serverError = error.message; this.update(); return; }
      this.busy = true;
      this.serverError = '';
      this.update();
      try {
        await addToDrawer({ items, drawer: document.querySelector('cart-drawer'), opener: this.button,
          action: this.form.action, pageUrl: window.location.href });
      } catch (error) { this.serverError = error.message; }
      finally { this.busy = false; if (this.isConnected) this.update(); }
    }
  }
  customElements.define('aven-contour-sets', AvenContourSets);
})();
